import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useStore } from '../../../state/zustand-store';
import { Messages } from '../../../../utils/message-constants';
import {
  createVaultMatter,
  updateVaultMatter,
  deleteVaultMatter,
  createVaultExport,
  deleteVaultExports,
  VaultKeys,
  VaultMatterModel,
  VaultExportModel,
} from '../../../../api/vault-api';
import { Page } from '../../../../../types';

interface CreateVaultMatterPayload {
  name: string;
  description: string;
  changeLogMessage: string;
}

interface UpdateVaultMatterPayload {
  matterId: string;
  name: string;
  description: string;
  changeLogMessage: string;
}

interface DeleteVaultMattersPayload {
  selectedMatterIds: string[];
  changeLogMessage: string;
}

interface CreateVaultExportPayload {
  matterId: string;
  model: Partial<VaultExportModel>;
  changeLogMessage: string;
}

interface DeleteVaultExportsPayload {
  matterId: string;
  selectedExportIds: string[];
  changeLogMessage: string;
}

export function useVaultActions() {
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const { mutate: createMatter, isPending: isCreatingMatter } = useMutation({
    mutationFn: ({
      name,
      description,
      changeLogMessage,
    }: CreateVaultMatterPayload) => {
      return createVaultMatter({ name, description }, changeLogMessage);
    },
    onSuccess: (data) => {
      queryClient.setQueryData<VaultMatterModel[]>(
        [VaultKeys.VaultMatters],
        (oldData) => {
          if (!oldData) return [];
          return [data, ...oldData];
        }
      );
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Created('Vault matter'),
      });
    },
  });

  const { mutate: updateMatter, isPending: isUpdatingMatter } = useMutation({
    mutationFn: ({
      matterId,
      name,
      description,
      changeLogMessage,
    }: UpdateVaultMatterPayload) => {
      return updateVaultMatter(
        matterId,
        { name, description },
        changeLogMessage
      );
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData<VaultMatterModel[]>(
        [VaultKeys.VaultMatters],
        (oldData) => {
          if (!oldData) return [];
          return oldData.map((m) =>
            m.matterId === variables.matterId ? { ...m, ...data } : m
          );
        }
      );
    },
  });

  const { mutate: deleteMatters, isPending: isDeletingMatters } = useMutation({
    mutationFn: ({
      selectedMatterIds,
      changeLogMessage,
    }: DeleteVaultMattersPayload) => {
      return deleteVaultMatter(selectedMatterIds, changeLogMessage);
    },
    onSuccess: (_, variables) => {
      queryClient.setQueryData<VaultMatterModel[]>(
        [VaultKeys.VaultMatters],
        (oldData) => {
          if (!oldData) return [];
          return oldData.filter(
            (m) => !variables.selectedMatterIds.includes(m.matterId)
          );
        }
      );
    },
  });

  const { mutate: createExport, isPending: isCreatingExport } = useMutation({
    mutationFn: ({
      matterId,
      model,
      changeLogMessage,
    }: CreateVaultExportPayload) => {
      const formattedModel = { ...model };

      if (
        formattedModel.startTime === 'invalid' ||
        formattedModel.endTime === 'invalid' ||
        formattedModel.methodTargetVersionDate === 'invalid'
      ) {
        throw new Error('Invalid date provided');
      }

      if (formattedModel.startTime) {
        formattedModel.startTime = `${formattedModel.startTime}T00:00:00Z`;
      } else {
        delete formattedModel.startTime;
      }
      if (formattedModel.endTime) {
        formattedModel.endTime = `${formattedModel.endTime}T23:59:59Z`;
      } else {
        delete formattedModel.endTime;
      }
      if (formattedModel.methodTargetVersionDate) {
        formattedModel.methodTargetVersionDate = `${formattedModel.methodTargetVersionDate}T00:00:00Z`;
      } else {
        delete formattedModel.methodTargetVersionDate;
      }
      if (
        !formattedModel.peopleQuery ||
        formattedModel.peopleQuery.length === 0
      ) {
        delete formattedModel.peopleQuery;
      }
      if (
        !formattedModel.locationQuery ||
        formattedModel.locationQuery.length === 0
      ) {
        delete formattedModel.locationQuery;
      }
      if (
        !formattedModel.minusWords ||
        formattedModel.minusWords.length === 0
      ) {
        delete formattedModel.minusWords;
      }
      if (
        !formattedModel.responseStatuses ||
        formattedModel.responseStatuses.length === 0
      ) {
        delete formattedModel.responseStatuses;
      }
      return createVaultExport(matterId, formattedModel, changeLogMessage);
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData<Page<VaultExportModel[]>>(
        [VaultKeys.VaultExports, variables.matterId, 100, undefined],
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            results: [data, ...oldData.results],
          };
        }
      );
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Created('Vault export'),
      });
    },
  });

  const { mutate: deleteExports, isPending: isDeletingExports } = useMutation({
    mutationFn: ({
      matterId,
      selectedExportIds,
      changeLogMessage,
    }: DeleteVaultExportsPayload) => {
      return deleteVaultExports(matterId, selectedExportIds, changeLogMessage);
    },
    onSuccess: (_, variables) => {
      const queries = queryClient.getQueriesData<Page<VaultExportModel[]>>({
        queryKey: [VaultKeys.VaultExports, variables.matterId],
      });

      queries.forEach(([queryKey, oldData]) => {
        if (oldData) {
          queryClient.setQueryData<Page<VaultExportModel[]>>(queryKey, {
            ...oldData,
            results: oldData.results.filter(
              (exportModel) =>
                !variables.selectedExportIds.includes(exportModel.id)
            ),
          });
        }
      });
    },
  });

  return {
    createMatter,
    isCreatingMatter,
    updateMatter,
    isUpdatingMatter,
    deleteMatters,
    isDeletingMatters,
    createExport,
    isCreatingExport,
    deleteExports,
    isDeletingExports,
  };
}
